import { decryptBackup, encryptBackup, type BaseRecord } from '@petrhythm/core';
import { belongsToPet, exportSnapshot, importSnapshot, type BackupSnapshot, type ImportResult } from '@petrhythm/data';

const SHARE_STATE_KEY = 'petrhythm.share_state';

export interface ShareState {
  petId: string;
  token: string;
  inviteId?: string;
  role: 'owner' | 'member';
  cursor: number;
  actorLabel?: string;
}

interface SharePayload {
  schemaVersion: 1;
  petId: string;
  tables: BackupSnapshot['tables'];
  attachments?: BackupSnapshot['attachments'];
}

export function readShareState(): ShareState | null {
  try {
    const value = JSON.parse(localStorage.getItem(SHARE_STATE_KEY) ?? 'null') as Partial<ShareState> | null;
    if (!value || typeof value.petId !== 'string' || typeof value.token !== 'string' || (value.role !== 'owner' && value.role !== 'member')) return null;
    return { petId: value.petId, token: value.token, ...(typeof value.inviteId === 'string' ? { inviteId: value.inviteId } : {}), ...(typeof value.actorLabel === 'string' ? { actorLabel: value.actorLabel.slice(0, 20) } : {}), role: value.role, cursor: typeof value.cursor === 'number' && Number.isSafeInteger(value.cursor) ? value.cursor : 0 };
  } catch {
    return null;
  }
}

/** Capture authorship at record creation, only for the connected pet. Never rewrite imported authors. */
export function recordActorLabel(petId: string): string { const state = readShareState(); return state?.petId === petId ? state.actorLabel || (state.role === 'member' ? '가족' : '보호자') : '보호자'; }

export function writeShareState(state: ShareState): void {
  localStorage.setItem(SHARE_STATE_KEY, JSON.stringify(state));
}

export function clearShareState(): void {
  localStorage.removeItem(SHARE_STATE_KEY);
  localStorage.removeItem('petrhythm.household-pending');
}

export function updateShareCursor(cursor: number): ShareState | null {
  const state = readShareState();
  if (!state) return null;
  const next = { ...state, cursor: Math.max(state.cursor, cursor) };
  writeShareState(next);
  return next;
}



function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

/** Build a pet-scoped encrypted delta. Plain records never leave this function. */
export async function createShareCiphertext(petId: string, token: string): Promise<string> {
  const snapshot = await exportSnapshot({petId});
  const tables: BackupSnapshot['tables'] = {};
  for (const [table, values] of Object.entries(snapshot.tables)) {
    tables[table as keyof BackupSnapshot['tables']] = (values ?? []).filter((value) => belongsToPet(table, value as BaseRecord, petId));
  }
  const attachmentIds = new Set<string>();
  for (const values of Object.values(tables)) for (const value of values ?? []) {
    for (const id of ((value as BaseRecord & { attachmentIds?: string[] }).attachmentIds ?? [])) attachmentIds.add(id);
    const photoBlobId = (value as BaseRecord & { photoBlobId?: string }).photoBlobId;
    if (photoBlobId) attachmentIds.add(photoBlobId);
  }
  const attachments = snapshot.attachments?.filter((attachment) => attachment.petId === petId && attachmentIds.has(attachment.id));
  const payload: SharePayload = { schemaVersion: 1, petId, tables, ...(attachments?.length ? { attachments } : {}) };
  const encrypted = await encryptBackup(JSON.stringify(payload), token);
  return toBase64(encrypted);
}

/** Decrypt and merge a server delta. importSnapshot keeps the newest updatedAt per record. */
export async function applyShareCiphertext(ciphertext: string, token: string, _actorLabel?: string, expectedPetId?: string, isCurrent?: () => boolean): Promise<ImportResult> {
  const epoch = typeof localStorage === 'undefined' ? null : localStorage.getItem('petrhythm.purge_epoch');
  const assertCurrent = () => { if ((typeof localStorage !== 'undefined' && localStorage.getItem('petrhythm.purge_epoch') !== epoch) || isCurrent?.() === false) throw new Error('삭제되거나 연결이 바뀐 공유 기록은 적용하지 않아요.'); };
  const plain = await decryptBackup(fromBase64(ciphertext), token);
  const payload = JSON.parse(new TextDecoder().decode(plain)) as Partial<SharePayload>;
  if (payload.schemaVersion !== 1 || typeof payload.petId !== 'string' || !payload.tables || typeof payload.tables !== 'object') throw new Error('공유 기록 형식을 읽지 못했어요.');
  if (expectedPetId && payload.petId !== expectedPetId) throw new Error('다른 아이의 공유 기록은 적용하지 않아요.');
  for (const [table, values] of Object.entries(payload.tables)) {
    if (!Array.isArray(values) || values.some(value => !value || !belongsToPet(table, value as BaseRecord, payload.petId!))) throw new Error('공유 범위 밖의 기록은 적용하지 않아요.');
  }
  if (payload.attachments?.some(value => value.petId !== payload.petId)) throw new Error('공유 범위 밖의 사진은 적용하지 않아요.');
  const tables = { ...payload.tables };
  // The feed actor uploaded a whole snapshot; they are not necessarily the
  // author of every record in it. Never rewrite provenance or the same revision.
  return importSnapshot({ schemaVersion: 1, exportedAt: Date.now(), tables, ...(payload.attachments ? { attachments: payload.attachments } : {}) }, { remember: false, assertCurrent });
}
